import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { BatteryWarning, XCircle, AlertTriangle, BrainCircuit } from 'lucide-react';
export function PainSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <section className="py-24 px-6 md:px-12 bg-[#111827] text-[#f5f5f0]" data-id="element-106">
      <div className="max-w-4xl mx-auto" data-id="element-107">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="space-y-16" data-id="element-108">
          {/* The Lie */}
          <motion.div variants={itemVariants} className="text-center space-y-8" data-id="element-109">
            <h2 className="text-3xl md:text-4xl font-bold leading-tight" data-id="element-110">
              Esto no va de comida. <br data-id="element-111" />
              <span className="text-gray-400" data-id="element-112">
                Va de la hora en que te quedas sin frenos.
              </span>
            </h2>

            <div className="bg-[#0a0e1a] border-l-4 border-amber-500 p-8 rounded-r-xl max-w-2xl mx-auto text-left shadow-lg" data-id="element-113">
              <p className="text-gray-400 text-sm uppercase tracking-widest mb-2" data-id="element-114">
                La mentira que te repites
              </p>
              <p className="text-2xl font-serif italic text-white" data-id="element-115">
                "Es que no tengo disciplina."
              </p>
            </div>

            <div className="text-lg md:text-xl leading-relaxed text-gray-300 space-y-6 max-w-3xl mx-auto text-left" data-id="element-116">
              <p data-id="element-117">
                La verdad suele ser más simple: estás intentando ganar el
                momento más difícil del día con la herramienta que menos
                funciona en ese momento.
              </p>
              <p data-id="element-118">
                De noche estás cansado, estás solo (aunque haya gente), te queda
                poca energía mental y tu cerebro quiere alivio inmediato.
              </p>
              <p className="font-medium text-amber-100" data-id="element-119">
                En ese contexto, "disciplina" es como pedirle a un móvil al 2%
                que corra un videojuego.
              </p>
            </div>
          </motion.div>

          {/* The Pattern */}
          <motion.div variants={itemVariants} className="bg-[#1a1f2e] rounded-2xl p-8 md:p-12 border border-gray-800" data-id="element-120">
            <h3 className="text-2xl font-bold mb-8 text-amber-500" data-id="element-121">
              El patrón exacto: "de día controlo / de noche me pierdo"
            </h3>

            <div className="space-y-8" data-id="element-122">
              <div className="flex gap-4" data-id="element-123">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-900/30 flex items-center justify-center text-green-500" data-id="element-124">
                  <span className="text-lg" data-id="element-125">☀</span>
                </div>
                <div data-id="element-126">
                  <h4 className="font-bold text-white mb-2" data-id="element-127">Durante el día</h4>
                  <p className="text-gray-400" data-id="element-128">
                    Aguantas. Comes "bien". Incluso te sientes orgulloso. Llegas
                    a la noche pensando: "Hoy sí".
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-129">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-900/30 flex items-center justify-center text-amber-500" data-id="element-130">
                  <span className="text-lg" data-id="element-131">⚡</span>
                </div>
                <div data-id="element-132">
                  <h4 className="font-bold text-white mb-2" data-id="element-133">
                    El detonante invisible
                  </h4>
                  <p className="text-gray-400" data-id="element-134">
                    Un cansancio raro, una emoción incómoda, un silencio, una
                    serie... y de repente ya estás comiendo. No lo decides.
                    Simplemente ocurre.
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-135">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-900/30 flex items-center justify-center text-red-500" data-id="element-136">
                  <span className="text-lg" data-id="element-137">⚠</span>
                </div>
                <div data-id="element-138">
                  <h4 className="font-bold text-white mb-2" data-id="element-139">
                    El castigo (fertilizante del atracón)
                  </h4>
                  <p className="text-gray-400" data-id="element-140">
                    "Mañana no ceno", "mañana compenso". Ese castigo es lo que
                    garantiza que mañana volverás a caer.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Why Old Solutions Fail */}
          <motion.div variants={itemVariants} data-id="element-141">
            {/* Emotional photo */}
            <div className="mb-10 rounded-2xl overflow-hidden border border-gray-800 shadow-2xl shadow-black/40" data-id="element-142">
              <img src="https://cdn.magicpatterns.com/uploads/bHFXz1JaDJpnENwsDHUwv6/foto3-salesletter-variante.png" alt="" className="w-full h-auto object-cover" data-id="element-143" />
            </div>

            <h3 className="text-2xl md:text-3xl font-bold mb-10 text-center" data-id="element-144">
              Por qué lo que has probado falla
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-145">
              {[{
              icon: <XCircle className="w-6 h-6 text-amber-500" data-id="element-146" />,
              title: 'Prohibiciones',
              desc: 'Cuando prohíbes, no eliminas el deseo: lo inflas.'
            }, {
              icon: <BatteryWarning className="w-6 h-6 text-amber-500" data-id="element-147" />,
              title: 'Restricción',
              desc: 'Si llegas vacío a la noche, la noche te cobra.'
            }, {
              icon: <BrainCircuit className="w-6 h-6 text-amber-500" data-id="element-148" />,
              title: 'Apps y consejos genéricos',
              desc: 'Te dicen qué hacer cuando estás bien. No te dicen qué hacer cuando estás a punto de romper.'
            }, {
              icon: <AlertTriangle className="w-6 h-6 text-amber-500" data-id="element-149" />,
              title: 'Castigo post-atracón',
              desc: 'Te deja más tenso, más hambriento, más obsesionado. Y te prepara una noche peor.'
            }].map((item, idx) => <div key={idx} className="bg-[#0a0e1a] p-6 rounded-xl border border-gray-800 hover:border-amber-900/50 transition-colors" data-id="element-150">
                  <div className="flex items-center gap-3 mb-3" data-id="element-151">
                    {item.icon}
                    <h4 className="font-bold text-lg text-white" data-id="element-152">
                      {item.title}
                    </h4>
                  </div>
                  <p className="text-gray-400 leading-relaxed" data-id="element-153">{item.desc}</p>
                </div>)}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}